/**
 * Telegram WebApp SDK helper. Loads the script and exposes utilities.
 * Falls back gracefully in browser (non-Telegram) contexts.
 */
function getTGParam(name) {
  if (typeof window === "undefined") return "";

  const fromSearch = new URLSearchParams(window.location.search).get(name);
  if (fromSearch) return fromSearch;

  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  return hashParams.get(name) || "";
}

export function getTG() {
  if (typeof window === "undefined") return null;
  return window.Telegram?.WebApp || null;
}

export function isInTelegram() {
  const tg = getTG();
  if (tg && tg.initData && tg.initData.length > 0) return true;

  // Fallbacks for cases where SDK init is delayed but Telegram launch params are already present.
  if (getTGParam("tgWebAppData")) return true;
  if (getTGParam("tgWebAppPlatform")) return true;

  if (typeof navigator !== "undefined") {
    const ua = navigator.userAgent || "";
    if (/Telegram/i.test(ua)) return true;
  }

  return false;
}

export function hasTelegramInitData() {
  return !!getInitData();
}

export function initTG() {
  const tg = getTG();
  if (!tg) return;
  try {
    tg.ready();
    tg.expand();
    // Without this, dragging down anywhere (e.g. pull-to-refresh attempts, or
    // just scrolling up past the top of a list) triggers Telegram's own
    // swipe-to-collapse gesture and closes the Mini App. Bot API 7.7+.
    if (typeof tg.disableVerticalSwipes === "function") {
      tg.disableVerticalSwipes();
    }
    // We use our own peach palette; don't attempt to override Telegram theme.
  } catch (e) {
    console.warn("TG init:", e);
  }
}

export function getInitData() {
  const fromSDK = getTG()?.initData || "";
  if (fromSDK) return fromSDK;

  return getTGParam("tgWebAppData") || "";
}

export function getTGUser() {
  return getTG()?.initDataUnsafe?.user || null;
}

export function getStartParam() {
  return getTG()?.initDataUnsafe?.start_param || null;
}

export function tgHapticImpact(style = "light") {
  try {
    getTG()?.HapticFeedback?.impactOccurred(style);
  } catch {}
}

export function tgHapticNotification(type = "success") {
  try {
    getTG()?.HapticFeedback?.notificationOccurred(type);
  } catch {}
}

export function tgShareLink(url, text = "") {
  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  const tg = getTG();
  if (tg?.openTelegramLink) {
    tg.openTelegramLink(shareUrl);
    return;
  }
  if (typeof window !== "undefined") window.open(shareUrl, "_blank");
}

export function tgOpenInvoice(url) {
  return new Promise((resolve, reject) => {
    const tg = getTG();
    if (!tg?.openInvoice) {
      reject(new Error("openInvoice unavailable"));
      return;
    }
    try {
      tg.openInvoice(url, (status) => resolve(status));
    } catch (e) {
      reject(e);
    }
  });
}

export function ensureTGScript() {
  if (typeof document === "undefined") return;
  if (document.getElementById("tg-web-app-script")) return;
  const s = document.createElement("script");
  s.id = "tg-web-app-script";
  s.src = "https://telegram.org/js/telegram-web-app.js";
  s.async = true;
  document.head.appendChild(s);
}
